import React, { useEffect, useMemo, useRef, useState } from "react";

// Deterministic pseudo-random waveform per track
export default function Waveform({
  videoId,
  progress = 0,
  color = "#d946ef",
  bars = 64,
  active = false,
  onSeek,
  markerFrac = null,
}) {
  const values = useMemo(() => {
    const seed = videoId ? [...videoId].reduce((a, c) => a + c.charCodeAt(0), 0) : 42;
    const arr = [];
    let x = seed;
    for (let i = 0; i < bars; i++) {
      x = (x * 9301 + 49297) % 233280;
      const v = 0.25 + (x / 233280) * 0.75;
      arr.push(v);
    }
    return arr;
  }, [videoId, bars]);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick((t) => t + 1), 120);
    return () => clearInterval(id);
  }, [active]);

  const containerRef = useRef(null);
  const [hoverFrac, setHoverFrac] = useState(null);
  const [dragFrac, setDragFrac] = useState(null); // while scrubbing
  const draggingRef = useRef(false);

  const fracFromClientX = (clientX) => {
    const el = containerRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };

  const fracFromEvent = (e) => {
    const clientX = (e.touches && e.touches[0]?.clientX) ?? e.clientX;
    return fracFromClientX(clientX);
  };

  const startDrag = (e) => {
    if (!onSeek || !videoId) return;
    draggingRef.current = true;
    const f = fracFromEvent(e);
    setDragFrac(f);
    e.preventDefault();
  };

  const endDrag = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const f = dragFrac !== null ? dragFrac : fracFromEvent(e);
    setDragFrac(null);
    onSeek(f);
  };

  const moveDrag = (e) => {
    if (!draggingRef.current) return;
    const f = fracFromEvent(e);
    setDragFrac(f);
    e.preventDefault();
  };

  // global mouse/touch listeners while dragging
  useEffect(() => {
    if (!onSeek || !videoId) return;
    const onMove = (e) => moveDrag(e);
    const onUp = (e) => endDrag(e);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSeek, videoId, dragFrac]);

  const handleHover = (e) => {
    if (!onSeek || !videoId || draggingRef.current) return;
    setHoverFrac(fracFromEvent(e));
  };

  const seekable = !!(onSeek && videoId);
  const displayProgress = dragFrac !== null ? dragFrac : progress;
  const indicatorFrac = dragFrac !== null ? dragFrac : hoverFrac;

  return (
    <div
      ref={containerRef}
      onMouseDown={seekable ? startDrag : undefined}
      onMouseMove={seekable ? handleHover : undefined}
      onMouseLeave={() => setHoverFrac(null)}
      onTouchStart={seekable ? startDrag : undefined}
      className={`relative w-full h-16 flex items-end gap-[2px] px-1 select-none touch-none ${
        seekable ? "cursor-pointer" : ""
      }`}
      role={seekable ? "slider" : undefined}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(displayProgress * 100)}
      data-testid={`waveform-${videoId || "empty"}`}
    >
      {values.map((v, i) => {
        const isPlayed = i / bars < displayProgress;
        const wobble = active && dragFrac === null && Math.abs(i - (tick % bars)) < 3 ? 1.15 : 1;
        return (
          <div
            key={i}
            className="flex-1 rounded-sm transition-all pointer-events-none"
            style={{
              height: `${v * 100 * wobble}%`,
              background: isPlayed ? color : "rgba(255,255,255,0.12)",
              boxShadow: isPlayed ? `0 0 6px ${color}` : "none",
            }}
          />
        );
      })}
      {/* Hover / drag indicator */}
      {seekable && indicatorFrac !== null && (
        <div
          className="absolute top-0 bottom-0 pointer-events-none"
          style={{
            left: `${indicatorFrac * 100}%`,
            width: dragFrac !== null ? 2 : 1,
            background: dragFrac !== null ? color : "rgba(255,255,255,0.7)",
            boxShadow: `0 0 8px ${color}`,
          }}
        />
      )}
      {/* Drop-end marker */}
      {markerFrac !== null && markerFrac >= 0 && markerFrac <= 1 && (
        <>
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{
              left: `${markerFrac * 100}%`,
              width: 2,
              background: "#eab308",
              boxShadow: "0 0 8px #eab308",
            }}
          />
          <div
            className="absolute -top-1 pointer-events-none font-mono font-black text-[8px] tracking-widest"
            style={{
              left: `calc(${markerFrac * 100}% + 4px)`,
              color: "#eab308",
              textShadow: "0 0 4px rgba(0,0,0,0.8)",
            }}
          >
            DROP END
          </div>
        </>
      )}
    </div>
  );
}
